/**
 * SkuChangeConfirmDialog (BLU-1373, D-16). The ONE confirm both client wizard
 * saves that decide how products get SKUs (Tag products: what you sell;
 * Custom or ready-made: the per-type picks) open before they would change
 * that decision for an org that may already have synced products.
 *
 * Synced products get new SKUs at their next sync and orders already placed
 * keep theirs, so the action is reversible: orange outline confirm, never red
 * (DESIGN.md §13.6, mirrors `SupplySavedSummary`). Confirm runs the caller's
 * single save; Cancel saves nothing and leaves the picks on screen. Focus
 * returns to the step's Save button when the dialog closes.
 */
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface SkuChangeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs the save. The dialog closes itself first. */
  onConfirm: () => void;
  /** The control focus returns to on close (the step's Save button). */
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}

export function SkuChangeConfirmDialog({ open, onOpenChange, onConfirm, returnFocusRef }: SkuChangeConfirmDialogProps) {
  const { t } = useTranslation('onboarding');
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="motion-reduce:animate-none"
        onCloseAutoFocus={(event) => {
          if (returnFocusRef.current) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="[overflow-wrap:anywhere]">{t('skuChange.title')}</AlertDialogTitle>
          <AlertDialogDescription className="leading-normal [overflow-wrap:anywhere]">
            {t('skuChange.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('skuChange.cancel')}</AlertDialogCancel>
          {/* Reversible: orange outline, never red (DESIGN.md §13.6). */}
          <Button
            type="button"
            variant="outline"
            className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            {t('skuChange.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
